import { AbstractControl } from '@angular/forms';
import { combineLatest, Observable, of } from 'rxjs';
import { mapTo, skip, startWith, tap } from 'rxjs/operators';
import { UomAmount } from '../ports/auto-calc-ports';

/**
 * Property availability/vacancy TOTALS orchestration, moved verbatim from the host
 * `total-availability-space.service.ts`. The host keeps the catalog/UOM/store-coupled concerns and
 * supplies them as a focused {@link PropertyTotalsPorts} bundle (aligning with the Phase-1
 * `UomPort` / `MessagePort` shapes):
 *   - UOM-aware sum (`makeAmount`/`makeAmountWith`/`addition` + country-default UOM) -> `sumAmounts`,
 *   - `sumUOMsGetSquareFeet` -> `sumToSquareFeet`, `isEqual` -> `isEqual`,
 *   - `Store.dispatch(AddMessage.ColumnWarning / RemoveMessage.ByCode)` -> `addColumnWarning`/`removeByCode`.
 *
 * The pure orchestration (the combineLatest wiring + the 4 null-branches of `handleValueOneUpdate` +
 * the warning check) lives here; control resolution stays in the host service. Writes use the host's
 * DEFAULT `patchValue` (emitEvent: true — the total feeds downstream listeners) and `markAsDirty`
 * only (NO markAsTouched), exactly as the host.
 */
export interface PropertyTotalsPorts {
  /** UOM-aware sum: same uom -> a's uom; differing uoms -> the country-default space uom. */
  sumAmounts(a: UomAmount, b: UomAmount): UomAmount;
  /** Sum amounts and return the total in square feet (host `sumUOMsGetSquareFeet`). */
  sumToSquareFeet(amounts: ReadonlyArray<UomAmount>): number;
  /** Whether the total (value + uom) equals the inputs' square-foot sum (host `isEqual`). */
  isEqual(totalValue: unknown, totalUom: unknown, sumSquareFeet: number): boolean;
  /** Dispatch a column warning (host `Store.dispatch(AddMessage.ColumnWarning(columnId, code))`). */
  addColumnWarning(columnId: number, code: string): void;
  /** Clear a warning by code (host `Store.dispatch(RemoveMessage.ByCode(code))`). */
  removeByCode(code: string): void;
}

export class PropertyTotalsBinder {
  constructor(private readonly ports: PropertyTotalsPorts) {}

  /**
   * Total = ctrlOne (+) ctrlTwo, UOM-aware, written to (totalCtrl, totalCtrlUom). The total controls
   * are passed through with `of(...)` (not triggers); `skip(1)` drops the combineLatest seed emit.
   */
  makeTotalListener(...controls: AbstractControl[]): Observable<unknown> {
    const [ctrlOne, ctrlOneUom, ctrlTwo, ctrlTwoUom, totalCtrl, totalCtrlUom] = controls;
    return combineLatest([
      this.controlValueChanges(ctrlOne),
      this.controlValueChanges(ctrlOneUom),
      this.controlValueChanges(ctrlTwo),
      this.controlValueChanges(ctrlTwoUom),
      of(totalCtrl),
      of(totalCtrlUom),
    ]).pipe(
      skip(1), // skip initialization emitted value for combineLatest
      tap(streamControls => {
        const [c1, c1Uom, c2, c2Uom, tot, totUom] = streamControls as AbstractControl[];
        this.handleValueOneUpdate(c1, c1Uom, c2, c2Uom, tot, totUom);
      })
    );
  }

  /** Raise/clear a column warning when the inputs' square-foot sum does not equal the total. */
  checkForWarning(message: string, totalColumnId: number, ...controls: AbstractControl[]): Observable<unknown> {
    const [ctrlOne, ctrlOneUom, ctrlTwo, ctrlTwoUom, totalCtrl, totalCtrlUom] = controls;
    return combineLatest([
      this.controlValueChanges(ctrlOne),
      this.controlValueChanges(ctrlOneUom),
      this.controlValueChanges(ctrlTwo),
      this.controlValueChanges(ctrlTwoUom),
      this.controlValueChanges(totalCtrl),
      this.controlValueChanges(totalCtrlUom),
    ]).pipe(tap(streamControls => this.checkForWarningValidation(message, totalColumnId, ...(streamControls as AbstractControl[]))));
  }

  private checkForWarningValidation(message: string, totalColumnId: number, ...controls: AbstractControl[]): void {
    const [ctrlOne, ctrlOneUom, ctrlTwo, ctrlTwoUom, totalCtrl, totalCtrlUom] = controls;
    const sumValues = this.ports.sumToSquareFeet([
      { value: ctrlOne.value, uom: ctrlOneUom.value },
      { value: ctrlTwo.value, uom: ctrlTwoUom.value },
    ]);
    const equal = this.ports.isEqual(totalCtrl.value, totalCtrlUom.value, sumValues);
    if (equal) {
      this.ports.removeByCode(message);
    } else {
      this.ports.addColumnWarning(totalColumnId, message);
    }
  }

  private controlValueChanges(ctrl: AbstractControl): Observable<AbstractControl> {
    return ctrl.valueChanges.pipe(startWith(ctrl.value), mapTo(ctrl));
  }

  private handleValueOneUpdate(
    valueOneCtrl: AbstractControl,
    valueOneUOMCtrl: AbstractControl,
    valueTwoCtrl: AbstractControl,
    valueTwoUOMCtrl: AbstractControl,
    totalValCtrl: AbstractControl,
    totalValUOMCtrl: AbstractControl
  ): void {
    const valueOne = valueOneCtrl.value;
    const valueOneUOM = valueOneUOMCtrl.value;
    const valueTwo = valueTwoCtrl.value;
    const valueTwoUOM = valueTwoUOMCtrl.value;
    // If all the values are null, then set the total values to null as well.
    if (valueOne == null && valueOneUOM == null && valueTwo == null && valueTwoUOM == null) {
      totalValCtrl.patchValue(null);
      totalValUOMCtrl.patchValue(null);
      totalValCtrl.markAsDirty();
      totalValUOMCtrl.markAsDirty();
      // If the value and UOM of first inputs are null set the total to the values in second input.
    } else if (valueOne == null && valueOneUOM == null && valueTwo != null && valueTwoUOM != null) {
      totalValCtrl.patchValue(valueTwo);
      totalValUOMCtrl.patchValue(valueTwoUOM);
      totalValCtrl.markAsDirty();
      totalValUOMCtrl.markAsDirty();
      // If the value and UOM of second inputs are null set the total to the values in first input.
    } else if (valueTwo == null && valueTwoUOM == null && valueOne != null && valueOneUOM != null) {
      totalValCtrl.patchValue(valueOne);
      totalValUOMCtrl.patchValue(valueOneUOM);
      totalValCtrl.markAsDirty();
      totalValUOMCtrl.markAsDirty();
      // If all the inputs are not null: UOM-aware sum (same uom -> that uom; else country-default uom).
    } else if (valueOne != null && valueOneUOM != null && valueTwo != null && valueTwoUOM != null) {
      const sum = this.ports.sumAmounts({ value: valueOne, uom: valueOneUOM }, { value: valueTwo, uom: valueTwoUOM });
      totalValCtrl.patchValue(sum.value);
      totalValUOMCtrl.patchValue(sum.uom);
      totalValCtrl.markAsDirty();
      totalValUOMCtrl.markAsDirty();
    }
  }
}
